import { Head, Link, useForm } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface Props {
  message: {
    id: number
    name: string
    subject: string
    body: string
    notes: string
  }
  errors?: Record<string, string[]>
}

export default function Edit({ message, errors }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Manage", href: "/admin/messages" },
    { title: "Form Letters", href: "/admin/messages" },
    { title: "Edit", href: `/admin/messages/${message.id}/edit` },
  ]

  const { data, setData, patch, processing, transform } = useForm({
    name: message.name,
    subject: message.subject,
    body: message.body,
    notes: message.notes,
  })

  transform((data) => ({ message: data }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    patch(`/admin/messages/${message.id}`)
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Edit Form Letter" />

      <div className="p-6">
        <h1 className="mb-4 text-2xl font-semibold">Update Form Letter</h1>

        <form onSubmit={handleSubmit} className="max-w-2xl">
          <div className="mb-4">
            <label htmlFor="name" className="mb-1 block font-medium">
              Name
            </label>
            <input
              type="text"
              id="name"
              value={data.name}
              onChange={(e) => setData("name", e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
              maxLength={50}
            />
            {errors?.name && (
              <p className="text-hpi-red mt-1 text-sm">
                {errors.name.join(", ")}
              </p>
            )}
          </div>

          <div className="mb-4">
            <label htmlFor="subject" className="mb-1 block font-medium">
              Subject
            </label>
            <input
              type="text"
              id="subject"
              value={data.subject}
              onChange={(e) => setData("subject", e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
              maxLength={100}
            />
            {errors?.subject && (
              <p className="text-hpi-red mt-1 text-sm">
                {errors.subject.join(", ")}
              </p>
            )}
          </div>

          <div className="mb-4">
            <label htmlFor="body" className="mb-1 block font-medium">
              Text
            </label>
            <textarea
              id="body"
              value={data.body}
              onChange={(e) => setData("body", e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
              rows={10}
            />
            <p className="text-hpi-secondary mt-1 text-sm">
              Optional Placeholders: &nbsp;
              <code className="bg-hpi-pale-blue rounded px-1">
                [[RecipientFirst]]
              </code>{" "}
              &nbsp;
              <code className="bg-hpi-pale-blue rounded px-1">
                [[RecipientLast]]
              </code>{" "}
              &nbsp;
              <code className="bg-hpi-pale-blue rounded px-1">
                [[RecipientCompany]]
              </code>
            </p>
            <p className="text-hpi-red mt-1 text-sm">
              Placeholder replacement is exact match — brackets &amp;
              capitalization must match exactly. Placeholders will be replaced
              in Subject &amp; Body.
            </p>
            <p className="text-hpi-secondary mt-1 text-sm">
              Sender signature will be appended to the end of the message.
            </p>
            {errors?.body && (
              <p className="text-hpi-red mt-1 text-sm">
                {errors.body.join(", ")}
              </p>
            )}
          </div>

          <div className="mb-4">
            <label htmlFor="notes" className="mb-1 block font-medium">
              Notes
            </label>
            <textarea
              id="notes"
              value={data.notes}
              onChange={(e) => setData("notes", e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
              rows={4}
            />
            {errors?.notes && (
              <p className="text-hpi-red mt-1 text-sm">
                {errors.notes.join(", ")}
              </p>
            )}
          </div>

          <div className="flex gap-2">
            <button
              type="submit"
              disabled={processing}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
            >
              Save
            </button>
            <Link
              href="/admin/messages"
              className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
            >
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </AppLayout>
  )
}
